// frontend/src/utils/animationTiming.js
import { CONFIG } from './constants';
import useCinematicStore from '@store/useCinematicStore';

/**
 * Calculate milliseconds duration based on tick count and current playback speed
 * This is the SINGLE source of truth for all animation timing calculations
 * 
 * @param {number} ticks - Number of ticks for the animation
 * @returns {number} Duration in milliseconds adjusted for playback speed
 */
export const calculateMSBasedOnTicks = (ticks) => {
  // Validate ticks parameter
  if (typeof ticks !== 'number' || isNaN(ticks)) {
    console.warn('[calculateMSBasedOnTicks] Invalid ticks value:', ticks);
    return 0;
  }
  
  // Get current playback speed (don't subscribe, just get current state)
  const playbackSpeed = useCinematicStore.getState().playbackSpeed;
  
  // Check if playbackSpeed exists
  if (!playbackSpeed || typeof playbackSpeed !== 'number') {
    console.warn('[calculateMSBasedOnTicks] playbackSpeed not set or invalid:', playbackSpeed);
    console.warn('[calculateMSBasedOnTicks] Using default speed of 1. Add playbackSpeed to useCinematicStore!');
    // Use 1 as emergency fallback but warn loudly
    const duration = (CONFIG.TICK_DURATION_MS * ticks) / 1;
    return duration;
  }
  
  // Check for valid TICK_DURATION_MS
  if (!CONFIG.TICK_DURATION_MS || typeof CONFIG.TICK_DURATION_MS !== 'number') {
    console.warn('[calculateMSBasedOnTicks] Invalid TICK_DURATION_MS:', CONFIG.TICK_DURATION_MS);
    return 0;
  }
  
  // Calculate actual duration: base tick duration * ticks / speed
  const duration = (CONFIG.TICK_DURATION_MS * ticks) / playbackSpeed;
  
  // Validate result
  if (isNaN(duration)) {
    console.warn('[calculateMSBasedOnTicks] Calculation resulted in NaN:', {
      ticks,
      playbackSpeed,
      TICK_DURATION_MS: CONFIG.TICK_DURATION_MS,
      duration
    });
    return 0;
  }
  
  return duration;
};

/**
 * Helper to get standard animation durations
 */
export const ANIMATION_DURATIONS = {
  STEP_WAIT: () => calculateMSBasedOnTicks(2),      // 2 ticks for step execution
  FADE_IN: () => calculateMSBasedOnTicks(5),        // 5 ticks for fade in
  FADE_OUT: () => calculateMSBasedOnTicks(5),       // 5 ticks for fade out
  TRANSITION_DELAY: () => calculateMSBasedOnTicks(5), // 5 ticks between animations
};